import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import {
  ChangelogComment,
  ChangelogCommentRepliesResponse,
  ChangelogCommentsResponse,
  useCreateComment,
  useDeleteComment,
  useUpdateComment,
} from "@/queries/changelog";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface UseCommentActionsParams {
  workspaceId: number;
  changelogId: number;
}

interface CreateCommentContext {
  previousComments: ChangelogCommentsResponse | undefined;
  previousReplies?: ChangelogCommentRepliesResponse | undefined;
  tempId: number;
  isTopLevelComment: boolean;
  repliesQueryKey?: (string | number | null)[];
}

interface UpdateCommentContext {
  previousComments: ChangelogCommentsResponse | undefined;
  previousRepliesMap: Map<string, ChangelogCommentRepliesResponse>;
  commentId: number;
}

interface DeleteCommentContext {
  previousComments: ChangelogCommentsResponse | undefined;
  previousRepliesMap: Map<string, ChangelogCommentRepliesResponse>;
  commentId: number;
  isTopLevel: boolean;
}

const useCommentActions = ({ workspaceId, changelogId }: UseCommentActionsParams) => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const [pendingCommentIds, setPendingCommentIds] = useState<Set<number>>(new Set());
  const [optimisticCommentIds, setOptimisticCommentIds] = useState<Set<number>>(new Set());

  const topLevelCommentsQueryKey = ["changelogComments", workspaceId, changelogId, 1, 50];

  const addPendingComment = useCallback((id: number) => {
    setPendingCommentIds(prev => new Set(prev).add(id));
  }, []);

  const removePendingComment = useCallback((id: number) => {
    setPendingCommentIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const addOptimisticComment = useCallback((id: number) => {
    setOptimisticCommentIds(prev => new Set(prev).add(id));
  }, []);

  const removeOptimisticComment = useCallback((id: number) => {
    setOptimisticCommentIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const createCommentMutation = useCreateComment({
    onMutate: async (variables): Promise<CreateCommentContext> => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: topLevelCommentsQueryKey });

      // Snapshot the previous value
      const previousComments =
        queryClient.getQueryData<ChangelogCommentsResponse>(topLevelCommentsQueryKey);

      const isTopLevelComment = variables.data.thread_id === null;

      if (isTopLevelComment) {
        // Generate a temporary ID for optimistic update
        const tempId = -Date.now();
        addOptimisticComment(tempId);

        // Optimistically add the new comment
        const optimisticComment: ChangelogComment = {
          id: tempId,
          user_id: user?.id ?? 0,
          changelog_id: changelogId,
          thread_id: null,
          text: variables.data.text,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          user: {
            email: user?.email ?? "",
            name: user?.name ?? "",
          },
          replies: 0,
        };

        queryClient.setQueryData<ChangelogCommentsResponse>(topLevelCommentsQueryKey, old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            items: [...old.items, optimisticComment],
            total: old.total + 1,
          };
        });

        return { previousComments, tempId, isTopLevelComment };
      }

      // For replies, cancel the replies query and add optimistic reply
      const repliesQueryKey = [
        "changelogCommentReplies",
        workspaceId,
        variables.data.thread_id,
        1,
        50,
      ];
      await queryClient.cancelQueries({ queryKey: repliesQueryKey });

      const previousReplies =
        queryClient.getQueryData<ChangelogCommentRepliesResponse>(repliesQueryKey);

      const tempId = -Date.now();
      addOptimisticComment(tempId);

      const optimisticReply: ChangelogComment = {
        id: tempId,
        user_id: user?.id ?? 0,
        changelog_id: changelogId,
        thread_id: variables.data.thread_id,
        text: variables.data.text,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user: {
          email: user?.email ?? "",
          name: user?.name ?? "",
        },
        replies: 0,
      };

      queryClient.setQueryData<ChangelogCommentRepliesResponse>(repliesQueryKey, old => {
        if (!old) {
          return {
            success: true,
            items: [optimisticReply],
            page: 1,
            pages: 1,
            total: 1,
            per_page: 50,
            has_next: false,
            has_prev: false,
          };
        }
        return {
          ...old,
          items: [...old.items, optimisticReply],
          total: old.total + 1,
        };
      });

      // Also update the parent comment's reply count
      queryClient.setQueryData<ChangelogCommentsResponse>(topLevelCommentsQueryKey, old => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          items: old.items.map(comment =>
            comment.id === variables.data.thread_id
              ? { ...comment, replies: comment.replies + 1 }
              : comment,
          ),
        };
      });

      return { previousComments, previousReplies, tempId, isTopLevelComment, repliesQueryKey };
    },
    onError: (error, _variables, context) => {
      const ctx = context as CreateCommentContext | undefined;
      // Rollback on error
      if (ctx?.previousComments) {
        queryClient.setQueryData(topLevelCommentsQueryKey, ctx.previousComments);
      }
      if (ctx && !ctx.isTopLevelComment && ctx.previousReplies && ctx.repliesQueryKey) {
        queryClient.setQueryData(ctx.repliesQueryKey, ctx.previousReplies);
      }
      if (ctx?.tempId) {
        removeOptimisticComment(ctx.tempId);
      }
      displayErrorToast(error);
    },
    onSuccess: (_data, _variables, context) => {
      const ctx = context as CreateCommentContext | undefined;
      if (ctx?.tempId) {
        removeOptimisticComment(ctx.tempId);
      }
    },
    onSettled: (_data, _error, variables) => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: topLevelCommentsQueryKey });
      if (variables.data.thread_id !== null) {
        queryClient.invalidateQueries({
          queryKey: ["changelogCommentReplies", workspaceId, variables.data.thread_id],
        });
      }
    },
  });

  const updateCommentMutation = useUpdateComment({
    onMutate: async (variables): Promise<UpdateCommentContext> => {
      addPendingComment(variables.commentId);

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: topLevelCommentsQueryKey });

      // Snapshot the previous value
      const previousComments =
        queryClient.getQueryData<ChangelogCommentsResponse>(topLevelCommentsQueryKey);

      // Optimistically update the comment
      queryClient.setQueryData<ChangelogCommentsResponse>(topLevelCommentsQueryKey, old => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          items: old.items.map(comment =>
            comment.id === variables.commentId
              ? { ...comment, text: variables.data.text, updated_at: new Date().toISOString() }
              : comment,
          ),
        };
      });

      // Also check replies caches
      const repliesQueries = queryClient.getQueriesData<ChangelogCommentRepliesResponse>({
        queryKey: ["changelogCommentReplies", workspaceId],
      });

      const previousRepliesMap = new Map<string, ChangelogCommentRepliesResponse>();

      for (const [queryKey, data] of repliesQueries) {
        if (data) {
          const keyString = JSON.stringify(queryKey);
          previousRepliesMap.set(keyString, data);

          queryClient.setQueryData<ChangelogCommentRepliesResponse>(queryKey as string[], old => {
            if (!old) {
              return old;
            }
            return {
              ...old,
              items: old.items.map(comment =>
                comment.id === variables.commentId
                  ? { ...comment, text: variables.data.text, updated_at: new Date().toISOString() }
                  : comment,
              ),
            };
          });
        }
      }

      return { previousComments, previousRepliesMap, commentId: variables.commentId };
    },
    onError: (error, _variables, context) => {
      const ctx = context as UpdateCommentContext | undefined;
      // Rollback on error
      if (ctx?.previousComments) {
        queryClient.setQueryData(topLevelCommentsQueryKey, ctx.previousComments);
      }
      if (ctx?.previousRepliesMap) {
        for (const [keyString, data] of ctx.previousRepliesMap) {
          const queryKey = JSON.parse(keyString) as string[];
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (ctx?.commentId) {
        removePendingComment(ctx.commentId);
      }
      displayErrorToast(error);
    },
    onSuccess: (_data, _variables, context) => {
      const ctx = context as UpdateCommentContext | undefined;
      if (ctx?.commentId) {
        removePendingComment(ctx.commentId);
      }
      displayToast("Comment updated", ToastType.success);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: topLevelCommentsQueryKey });
      queryClient.invalidateQueries({ queryKey: ["changelogCommentReplies", workspaceId] });
    },
  });

  const deleteCommentMutation = useDeleteComment({
    onMutate: async (variables): Promise<DeleteCommentContext> => {
      addPendingComment(variables.commentId);

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: topLevelCommentsQueryKey });

      // Snapshot the previous value
      const previousComments =
        queryClient.getQueryData<ChangelogCommentsResponse>(topLevelCommentsQueryKey);

      // Find the comment to check if it's a top-level comment or reply
      const comment = previousComments?.items.find(c => c.id === variables.commentId);
      const isTopLevel = !!comment;

      if (isTopLevel) {
        // Optimistically remove the comment
        queryClient.setQueryData<ChangelogCommentsResponse>(topLevelCommentsQueryKey, old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            items: old.items.filter(c => c.id !== variables.commentId),
            total: Math.max(0, old.total - 1),
          };
        });
      }

      // Also check replies caches
      const repliesQueries = queryClient.getQueriesData<ChangelogCommentRepliesResponse>({
        queryKey: ["changelogCommentReplies", workspaceId],
      });

      const previousRepliesMap = new Map<string, ChangelogCommentRepliesResponse>();
      let parentCommentId: number | null = null;

      for (const [queryKey, data] of repliesQueries) {
        if (data) {
          const keyString = JSON.stringify(queryKey);
          previousRepliesMap.set(keyString, data);

          const replyToDelete = data.items.find(c => c.id === variables.commentId);
          if (replyToDelete) {
            parentCommentId = replyToDelete.thread_id;

            queryClient.setQueryData<ChangelogCommentRepliesResponse>(queryKey as string[], old => {
              if (!old) {
                return old;
              }
              return {
                ...old,
                items: old.items.filter(c => c.id !== variables.commentId),
                total: Math.max(0, old.total - 1),
              };
            });
          }
        }
      }

      // Update the parent comment's reply count if it was a reply
      if (parentCommentId !== null) {
        queryClient.setQueryData<ChangelogCommentsResponse>(topLevelCommentsQueryKey, old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            items: old.items.map(c =>
              c.id === parentCommentId ? { ...c, replies: Math.max(0, c.replies - 1) } : c,
            ),
          };
        });
      }

      return {
        previousComments,
        previousRepliesMap,
        commentId: variables.commentId,
        isTopLevel,
      };
    },
    onError: (error, _variables, context) => {
      const ctx = context as DeleteCommentContext | undefined;
      // Rollback on error
      if (ctx?.previousComments) {
        queryClient.setQueryData(topLevelCommentsQueryKey, ctx.previousComments);
      }
      if (ctx?.previousRepliesMap) {
        for (const [keyString, data] of ctx.previousRepliesMap) {
          const queryKey = JSON.parse(keyString) as string[];
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (ctx?.commentId) {
        removePendingComment(ctx.commentId);
      }
      displayErrorToast(error);
    },
    onSuccess: (_data, _variables, context) => {
      const ctx = context as DeleteCommentContext | undefined;
      if (ctx?.commentId) {
        removePendingComment(ctx.commentId);
      }
      displayToast("Comment deleted", ToastType.success);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: topLevelCommentsQueryKey });
      queryClient.invalidateQueries({ queryKey: ["changelogCommentReplies", workspaceId] });
    },
  });

  const handleAddComment = async (text: string) => {
    await createCommentMutation.mutateAsync({
      workspaceId,
      data: {
        text,
        changelog_id: changelogId,
        thread_id: null,
      },
    });
  };

  const handleReply = async (parentId: number, text: string) => {
    await createCommentMutation.mutateAsync({
      workspaceId,
      data: {
        text,
        changelog_id: changelogId,
        thread_id: parentId,
      },
    });
  };

  const handleUpdateComment = async (commentId: number, text: string) => {
    await updateCommentMutation.mutateAsync({
      workspaceId,
      commentId,
      data: {
        text,
      },
    });
  };

  const handleDeleteComment = async (commentId: number) => {
    await deleteCommentMutation.mutateAsync({
      workspaceId,
      commentId,
    });
  };

  const isCommentPending = useCallback(
    (commentId: number) => pendingCommentIds.has(commentId),
    [pendingCommentIds],
  );

  const isCommentOptimistic = useCallback(
    (commentId: number) => optimisticCommentIds.has(commentId) || commentId < 0,
    [optimisticCommentIds],
  );

  return {
    handleReply,
    handleAddComment,
    handleUpdateComment,
    handleDeleteComment,
    isCreating: createCommentMutation.isPending,
    isUpdating: updateCommentMutation.isPending,
    isDeleting: deleteCommentMutation.isPending,
    isCommentPending,
    isCommentOptimistic,
    pendingCommentIds,
  };
};

export default useCommentActions;
